import { PropertyTypes } from "epsm-api-types";

import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	formatValue,
	formatValueNumber,
} from "@/components/widgets/helpers/FormattingHelpers";
import {
	COMPONENT_CONTAINER_CLASSES,
	EMPTY_VALUE,
	MIN_MAX_COLUMN_CLASSES,
	MIN_MAX_HEADER_COLUMN_CLASSES,
} from "@/components/widgets/Revision/config";
import {
	arrowStyles,
	coloredPercentTD,
	coloredTextConsensusTD,
	decidedArrow,
} from "@/components/widgets/Revision/lib/helpers/index";
import type {
	DataTypes,
	RevisionExt,
} from "@/components/widgets/Revision/types";
import { TableWatermark } from "@/components/widgets/Revision/ui/components";
import { cn } from "@/lib/utils";
import sprites from "@/shared/assets/sprite.svg";
import { valueColor } from "../../../utils/RevisionTableUtils";

export const EstimatesRevision = ({ data }: { data?: RevisionExt }) => {
	const fhDispersion = data?.dp?.fh?.ee?.[0]?.dispersion;
	const nsDispersion = data?.dp?.ns?.ee?.[0]?.dispersion;
	const fhDispersion90d = data?.dp?.fh?.ee?.[1]?.dispersion;
	const nsDispersion90d = data?.dp?.ns?.ee?.[1]?.dispersion;

	const hasFhData = Object.values(data ?? {}).some(
		(val) => (val as DataTypes<unknown>).fh,
	);
	const hasZcData = Object.values(data ?? {}).some(
		(val) => (val as DataTypes<unknown>).ns,
	);

	if (!hasFhData && !hasZcData) return false;

	return (
		<div className={COMPONENT_CONTAINER_CLASSES}>
			<h3 className="text-light-gray text-lg">
				Analysts Estimates Revision Score (RS)
			</h3>

			<table
				className={cn("w-full text-light-gray text-right border-collapse")}
				id="estimates-revision-table"
			>
				<colgroup>
					<col className={MIN_MAX_HEADER_COLUMN_CLASSES} />
					{hasZcData && (
						<>
							<col className={MIN_MAX_COLUMN_CLASSES} />
							<col className={MIN_MAX_COLUMN_CLASSES} />
						</>
					)}
					{hasFhData && (
						<>
							<col className={MIN_MAX_COLUMN_CLASSES} />
							<col className={MIN_MAX_COLUMN_CLASSES} />
						</>
					)}
				</colgroup>
				<thead>
					<tr>
						<td
							className={cn(
								"text-left border-r-2 border-gray overflow-hidden text-ellipsis whitespace-nowrap",
							)}
						>
							RS Trend
						</td>
						{hasZcData && (
							<>
								<td className={cn("border-l-2 border-t-2 border-gray")}>
									<div className="flex justify-center">
										<svg
											aria-label="trend arrow"
											role="img"
											className={arrowStyles(data?.rs_chg?.ns?.trend_ed!)}
										>
											<use
												xlinkHref={`${sprites}#${decidedArrow(data?.rs_chg?.ns?.trend_ed!)}`}
											/>
										</svg>
									</div>
								</td>
								<td className={cn("border-r-2 border-t-2 border-gray")}>
									<div className="flex justify-center">
										<svg
											aria-label="trend arrow"
											role="img"
											className={arrowStyles(data?.rs_chg?.ns?.trend_90d!)}
										>
											<use
												xlinkHref={`${sprites}#${decidedArrow(data?.rs_chg?.ns?.trend_90d!)}`}
											/>
										</svg>
									</div>
								</td>
							</>
						)}

						{hasFhData && (
							<>
								<td className={cn("border-l-2 border-t-2 border-gray")}>
									<div className="flex justify-center">
										<svg
											aria-label="trend arrow"
											role="img"
											className={arrowStyles(data?.rs_chg?.fh?.trend_ed!)}
										>
											<use
												xlinkHref={`${sprites}#${decidedArrow(data?.rs_chg?.fh?.trend_ed!)}`}
											/>
										</svg>
									</div>
								</td>
								<td className={cn("border-r-2 border-t-2 border-gray")}>
									<div className="flex justify-center">
										<svg
											aria-label="trend arrow"
											role="img"
											className={`${arrowStyles(data?.rs_chg?.fh?.trend_90d!)}`}
										>
											<use
												xlinkHref={`${sprites}#${decidedArrow(data?.rs_chg?.fh?.trend_90d!)}`}
											/>
										</svg>
									</div>
								</td>
							</>
						)}
					</tr>
				</thead>
				<tbody>
					<tr className="border-2 border-gray">
						<td className="text-left border-r-2 border-gray">Earn. Date</td>
						{hasZcData && (
							<>
								<td>
									{data?.rs_chg?.ns?.extra_info?.earning_date &&
										formatValue(
											new Date(data?.rs_chg?.ns?.extra_info?.earning_date),
											PropertyTypes.DATE,
											{ month: "2-digit", day: "2-digit" },
											EMPTY_VALUE,
										)}
								</td>
								<td className="border-r-2 border-gray">90days</td>
							</>
						)}
						{hasFhData && (
							<>
								<td>
									{data?.rs_chg?.ns?.extra_info?.earning_date &&
										formatValue(
											new Date(data?.rs_chg?.ns?.extra_info?.earning_date),
											PropertyTypes.DATE,
											{ month: "2-digit", day: "2-digit" },
											EMPTY_VALUE,
										)}
								</td>
								<td>90days</td>
							</>
						)}
					</tr>

					<tr>
						<td className="text-left border-x-2 border-x-gray border-y-2 border-y-transparent">
							EPS RS Ch%
						</td>
						{hasZcData &&
							coloredPercentTD(data?.rs_chg?.ns?.pct_chg_ed, "border-r-0")}
						{hasZcData &&
							coloredPercentTD(
								data?.rs_chg?.ns?.pct_chg_90d,
								"border-l-0 border-r-2 border-d-0 border-gray",
							)}
						{hasFhData &&
							coloredPercentTD(data?.rs_chg?.fh?.pct_chg_ed, "border-r-0")}
						{hasFhData &&
							coloredPercentTD(
								data?.rs_chg?.fh?.pct_chg_90d,
								"border-r-2  border-gray",
							)}
					</tr>
					<tr>
						<td className="text-left border-x-2 border-x-gray border-y-2 border-y-transparent truncate">
							Sales RS Ch%
						</td>
						{hasZcData &&
							coloredPercentTD(data?.rss_chg?.ns?.pct_chg_ed, "border-r-0")}
						{hasZcData &&
							coloredPercentTD(
								data?.rss_chg?.ns?.pct_chg_90d,
								"border-l-0 border-r-2 border-gray",
							)}
						{hasFhData &&
							coloredPercentTD(data?.rss_chg?.fh?.pct_chg_ed, "border-r-0")}
						{hasFhData &&
							coloredPercentTD(
								data?.rss_chg?.fh?.pct_chg_90d,
								"border-r-2  border-gray",
							)}
					</tr>
					<tr>
						<td className="text-left border-2 border-gray truncate">
							Change in RS
						</td>
						{hasZcData && (
							<>
								<td
									className={cn(
										"border-l-2 border-r-0 border-d-2 border-gray",
										valueColor(data?.rs_chg?.ns?.abs_chg_ed),
									)}
								>
									{formatValueNumber(data?.rs_chg?.ns?.abs_chg_ed, EMPTY_VALUE)}
								</td>
								<td
									className={cn(
										"border-r-2 border-gray",
										valueColor(data?.rs_chg?.ns?.abs_chg_90d),
									)}
								>
									{formatValueNumber(
										data?.rs_chg?.ns?.abs_chg_90d,
										EMPTY_VALUE,
									)}
								</td>
							</>
						)}
						{hasFhData && (
							<>
								<td
									className={cn(
										"border-l-2 border-r-0 border-gray",
										valueColor(data?.rs_chg?.fh?.abs_chg_ed),
									)}
								>
									{formatValueNumber(data?.rs_chg?.fh?.abs_chg_ed, EMPTY_VALUE)}
								</td>
								<td
									className={cn(
										"border-r-2 border-gray",
										valueColor(data?.rs_chg?.fh?.abs_chg_90d),
									)}
								>
									{formatValueNumber(
										data?.rs_chg?.fh?.abs_chg_90d,
										EMPTY_VALUE,
									)}
								</td>
							</>
						)}
					</tr>
					<tr>
						<td className="text-left border-2 border-gray">Consensus</td>
						{hasZcData &&
							coloredTextConsensusTD(
								nsDispersion,
								"border-r-0 border-d-2 truncate",
							)}
						{hasZcData &&
							coloredTextConsensusTD(
								nsDispersion90d,
								"border-l-0 border-r-2 border-gray truncate",
							)}
						{hasFhData &&
							coloredTextConsensusTD(
								fhDispersion,
								"border-r-0 border-d-2 truncate",
							)}
						{hasFhData &&
							coloredTextConsensusTD(
								fhDispersion90d,
								"border-l-0 border-r-2  border-gray truncate",
							)}
					</tr>
					<tr className="text-[8px] border-l-2 border-l-transparent">
						<td className="border-l-2 border-l-transparent">&nbsp;</td>
						{hasZcData && (
							<td colSpan={2} className="border-2 border-gray text-center">
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="cursor-pointer">Source 1</span>
									</TooltipTrigger>
									<TooltipContent className="text-xs">
										Based on Estimates from Zacks
									</TooltipContent>
								</Tooltip>
							</td>
						)}
						{hasFhData && (
							<td colSpan={2} className="border-2 border-gray text-center">
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="cursor-pointer">Source 2</span>
									</TooltipTrigger>
									<TooltipContent className="text-xs">
										Based on Estimates from Finnhub
									</TooltipContent>
								</Tooltip>
							</td>
						)}
					</tr>
				</tbody>
			</table>

			<TableWatermark />
		</div>
	);
};
